/**
 * Change first character in string to lower case
 * @param {string} input
 * @returns {string}
 */
export const firstCharToLowerCase = (input: string): string => input.charAt(0).toLocaleLowerCase() + input.slice(1)

/**
 * Change first character in string to upper case
 * @param {string} input
 * @returns {string}
 */
export const firstCharToUpperCase = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1)

/**
 * get last [limit] character(s) of the string
 *
 * @param {string} input
 * @param {number} [limit=4]
 * @returns {string}
 */
export const getLast = (input: string | undefined, limit: number = 4): string => {
  if (isUndefinedOrEmpty(input)) {
    return ''
  }
  return input?.slice(-limit) ?? ''
}
/**
 * title case a string
 *
 * @param {string} input
 * @returns {string}
 */
export const titleCase = (input?: string | null): string => {
  if (input == null) {
    return ''
  }
  if (isUndefinedOrEmpty(input)) {
    return ''
  }
  return input.toLowerCase().split(' ').map(firstCharToUpperCase).join(' ')
}

/**
 * camel case a string
 *
 * @param {string} input
 * @returns {string}
 */
export const camelCase = (input: string | null | undefined): string => {
  if (input == null) {
    return ''
  }
  if (isUndefinedOrEmpty(input)) {
    return ''
  }

  const normalisedInput = input === input.toUpperCase() ? input.toLowerCase() : input
  const inputStr = firstCharToLowerCase(normalisedInput)

  const splitRegexp = /([^A-Za-z])/
  const inputSplitList = inputStr.split(splitRegexp).map((str) => str.trim())
  if (inputSplitList.length > 1) {
    const [_, delimiter] = inputSplitList
    return inputSplitList
      .filter((str) => str !== delimiter)
      .map((str, index) => {
        if (index > 0) {
          return firstCharToUpperCase(str.toLowerCase())
        }
        return firstCharToLowerCase(str.toLowerCase())
      })
      .join('')
  }

  return inputStr.trim()
}

/**
 * pascal case a string
 *
 * @param {string} input
 * @returns {string}
 */
export const pascalCase = (input: string): string => {
  if (isUndefinedOrEmpty(input)) {
    return ''
  }
  const camelCaseInput = camelCase(input)
  return firstCharToUpperCase(camelCaseInput)
}

/**
 * reverse a string
 *
 * @param {string} input
 * @returns {string}
 */
export const reverse = (input: string): string => input.split('').reverse().join('')

/**
 * join list of strings
 *
 * @param {string[]} array
 * @returns {string}
 */
export const joinStrings = (array: string[]): string => {
  return array.filter(Boolean).join(' ').replace(/  +/g, ' ').trim()
}

export const formatPercentage = (amount: string): string => `${parseFloat(amount) * 100}%`

export const first = (str: string | string[]): string | undefined => {
  if (Array.isArray(str)) {
    return str[0]
  }

  return str
}

export const isUndefinedOrEmpty = (input?: string | null): boolean => {
  return (input ?? '').trim().length === 0
}

/**
 * Convert string to boolean
 *
 * @param {string} value
 * @returns {boolean}
 */
export const stringToBoolean = (value: string): boolean => {
  return value === 'true'
}

/**
 * Convert boolean to string
 *
 * @param {boolean} value
 * @returns {string}
 */
export const booleanToString = (value: boolean): string => {
  return value.toString()
}

/**
 * Convert screaming snake to readable string
 * e.g. 'CREDIT_CARD' to 'credit card'
 * @param {string} value
 * @returns {string}
 */
export const snakeToString = (value: string): string => {
  return value.toLowerCase().replace('_', ' ')
}

export const StringObj = {
  camelCase,
  first,
  formatPercentage,
  getLast,
  isUndefinedOrEmpty,
  joinStrings,
  pascalCase,
  reverse,
  titleCase,
  stringToBoolean,
  booleanToString,
  snakeToString,
}
